import React, { useEffect, useRef, useState } from 'react';
import { Button, Space } from 'antd';
import { useTranslate } from '@/translations';

const formatDuration = (seconds: number) => {
  const safeSeconds = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = Math.floor(safeSeconds % 60);
  return `${minutes}:${remainder.toString().padStart(2, '0')}`;
};

interface VoiceNotePlayerProps {
  src: string;
  durationMs?: number;
  waveform?: number[];
}

export const VoiceNotePlayer = ({
  src,
  durationMs = 0,
  waveform = [],
}: VoiceNotePlayerProps) => {
  const { t } = useTranslate();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(durationMs / 1000);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    return () => audio?.pause();
  }, [src]);

  const toggle = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
      return;
    }
    try {
      await audio.play();
    } catch {
      setFailed(true);
    }
  };

  const seek = (seconds: number) => {
    const audio = audioRef.current;
    if (!audio || duration <= 0) return;
    const next = Math.max(0, Math.min(seconds, duration));
    audio.currentTime = next;
    setElapsed(next);
  };

  if (failed) return <span role="alert">{t('commsMediaUnavailable')}</span>;

  return (
    <Space direction="vertical" size="small" className="comms-voice-player">
      <audio
        ref={audioRef}
        src={src}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setElapsed(0);
        }}
        onError={() => setFailed(true)}
        onTimeUpdate={(event) => setElapsed(event.currentTarget.currentTime)}
        onLoadedMetadata={(event) => {
          if (Number.isFinite(event.currentTarget.duration))
            setDuration(event.currentTarget.duration);
        }}
      />
      <Space>
        <Button
          type="text"
          shape="circle"
          size="small"
          className="comms-voice-player__toggle"
          onClick={() => void toggle()}
          aria-label={t(playing ? 'commsPauseVoiceNote' : 'commsPlayVoiceNote')}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            {playing ? (
              <path d="M6 5h4v14H6zm8 0h4v14h-4z" />
            ) : (
              <path d="M8 5v14l11-7z" />
            )}
          </svg>
        </Button>
        <span>
          {formatDuration(elapsed)} / {formatDuration(duration)}
        </span>
      </Space>
      <span
        className="comms-voice-waveform"
        role="slider"
        tabIndex={0}
        aria-label={t('commsVoiceWaveform')}
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuenow={Math.round(elapsed)}
        onClick={(event) => {
          const bounds = event.currentTarget.getBoundingClientRect();
          if (bounds.width <= 0) return;
          seek(((event.clientX - bounds.left) / bounds.width) * duration);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowRight') seek(elapsed + 5);
          else if (event.key === 'ArrowLeft') seek(elapsed - 5);
          else if (event.key === 'Home') seek(0);
          else if (event.key === 'End') seek(duration);
          else return;
          event.preventDefault();
        }}
      >
        {waveform.map((value, index) => (
          <span
            key={index}
            data-played={
              duration > 0 &&
              (index + 1) / waveform.length <= elapsed / duration
            }
            style={{ height: `${Math.max(2, (value / 1024) * 24)}px` }}
          />
        ))}
      </span>
    </Space>
  );
};
